import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    useGetDistributorCompanyQuery,
    useGetRolesQuery,
    useGetUsersQuery,
    useResendInvitationMutation,
} from 'api/main';
import { userSelectors } from 'reducers';
import { userSchemas } from 'schemas';
import { useDebounce, useLocalStorageFilters } from 'utils/hooks';
import {
    listingActionButtonStyle,
    ORDER_BY,
    roleCategories,
    RoleNames,
    sortDirection,
    userAvailabilityStatus,
    UserEnrichments,
} from 'utils/constants';
import { getRenderRoleName, getUserIdFromIri, parseDate, parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    ActionsMenu,
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    OnlineBadge,
    QuickSearchField,
    StyledTableRow,
} from 'components';
import {
    MenuChangeRole,
    MenuDeleteUser,
    MenuDeactivate,
    MenuSendMessage,
    MenuSubstitute,
    MenuResendInvitation,
} from 'components/ActionsMenu/actions';
import {
    Typography,
    TableCell,
    TableBody,
    Table,
    TableContainer,
    TableFooter,
    TableRow,
    Box,
} from '@mui/material';

const getUserStatus = status => {
    switch (status) {
        case userAvailabilityStatus.ACTIVE:
            return texts.ACTIVATED;
        case userAvailabilityStatus.INACTIVE:
            return texts.DEACTIVATED;
        case userAvailabilityStatus.PENDING_PASSWORD_CHANGE:
            return texts.SENT;
        default:
            return texts.DEACTIVATED;
    }
};

const tableHeads = [
    { id: 'online', width: '5%', isSortable: false, label: texts.ONLINE },
    { id: ORDER_BY.lastName, width: '17%', isSortable: true, label: texts.NOM },
    { id: ORDER_BY.email, width: '15%', isSortable: true, label: texts.ADRESSE_EMAIL },
    { id: 'phoneNumber', width: '12%', isSortable: false, label: texts.PHONE_NUMBER },
    { id: ORDER_BY.role, width: '14%', isSortable: true, label: texts.ROLE },
    { id: 'tag', width: '11%', isSortable: false, label: texts.COMPLEMENT },
    { id: 'status', width: '9%', isSortable: false, label: texts.STATUT },
    { id: ORDER_BY.login, width: '12%', isSortable: true, label: texts.LAST_LOGIN_DATE },
    { id: 'actions', width: '5%', isSortable: false, label: '' },
];

const DeleteUserItem = React.forwardRef((props, ref) => <MenuDeleteUser ref={ref} {...props} />);
const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);
const ChangeRoleItem = React.forwardRef((props, ref) => <MenuChangeRole ref={ref} {...props} />);

const DistributorUserRow = ({ user, role, internalUser, companyDetails, ...subscription }) => {
    const isAccountNotConfirmed = useMemo(
        () => user?.availabilityStatus === userAvailabilityStatus.PENDING_PASSWORD_CHANGE,
        [user]
    );

    const [resendInvitation, { isLoading: resendLoader }] = useResendInvitationMutation();

    const impersonatedByUserId = useMemo(
        () =>
            internalUser?.impersonatedByUser
                ? getUserIdFromIri(internalUser.impersonatedByUser)
                : null,
        [internalUser]
    );

    const handleResendInvitation = useCallback(() => {
        resendInvitation(user.email);
    }, [user, resendInvitation]);

    const isMainUser = companyDetails?.user?.id === user.id;

    const canChangeRole = useMemo(
        () =>
            [RoleNames.PSI, RoleNames.ADMIN_CIF].includes(
                internalUser?.subscriptions?.[0]?.role?.name
            ) &&
            user.id !== internalUser?.id &&
            !isMainUser,
        [internalUser, isMainUser, user.id]
    );

    return (
        <StyledTableRow key={user.id}>
            <TableCell>
                <OnlineBadge online={user?.online} />
            </TableCell>
            <TableCell>{parseFullName(user)}</TableCell>
            <TableCell>{user?.email}</TableCell>
            <TableCell>{user.phoneNumber || '-'}</TableCell>
            <TableCell>
                <Typography>{getRenderRoleName(role)}</Typography>
                <Typography>{isMainUser ? texts.DISTRIBUTOR_MAIN_USER : ''}</Typography>
            </TableCell>
            <TableCell>{user?.tag || '-'}</TableCell>
            <TableCell>{getUserStatus(user?.availabilityStatus)}</TableCell>
            <TableCell>{parseDate(user?.lastLoggedAt) || '-'}</TableCell>
            <TableCell>
                <ActionsMenu>
                    <DeleteUserItem id={user.id} />
                    <ImpersonateItem
                        user={user}
                        disabled={!user.emailConfirmed}
                        originalPath="/distributor-users"
                        itemText={texts.IMPERSONATE_DISTRIBUTOR}
                    />
                    <MenuSendMessage receiver={user} />
                    <MenuDeactivate
                        id={user.id}
                        impersonatedByUserId={impersonatedByUserId}
                        currentUserId={internalUser.id}
                        status={user.availabilityStatus}
                    />
                    {isAccountNotConfirmed && (
                        <MenuResendInvitation
                            onClick={handleResendInvitation}
                            loading={resendLoader}
                            label={texts.RESEND_INVITATION}
                        />
                    )}
                    {canChangeRole && (
                        <ChangeRoleItem user={user} userSubscription={{ ...subscription, role }} />
                    )}
                </ActionsMenu>
            </TableCell>
        </StyledTableRow>
    );
};

const DistributorUsersTable = () => {
    const navigate = useNavigate();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);

    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: companyDetails = {}, isLoading: companyLoading } = useGetDistributorCompanyQuery(
        currentSubscription?.distributorCompany?.id,
        {
            skip: !currentSubscription?.distributorCompany?.id,
        }
    );

    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);
    const adminDistributorRoles = useMemo(
        () =>
            roles.filter(
                r =>
                    r.category?.name === roleCategories.ADMIN_DISTRIBUTORS ||
                    r.category?.name === roleCategories.DISTRIBUTORS
            ),
        [roles]
    );
    const defaultFilters = useMemo(
        () => ({
            page: 1,
            orderBy: ORDER_BY.id,
            direction: sortDirection.DESC,
            companyId: [currentSubscription?.distributorCompany?.id],
            keyword: '',
            role: [],
            enrichWith: [UserEnrichments.ROLES, UserEnrichments.DISTRIBUTOR_COMPANY],
        }),
        [currentSubscription]
    );

    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);
    const [filters, setFilters] = useLocalStorageFilters(
        'distributorUsersFilters',
        defaultFilters,
        activeImpersonation
    );
    const {
        data: { userList = [], lastPage } = { userList: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetUsersQuery(
        userSchemas.getUsers({
            ...filters,
            role: adminDistributorRoles.map(role => role.id),
        }),
        { skip: !roles.length }
    );

    const handlePageChange = useCallback(
        page => () => setFilters(oldValue => ({ ...oldValue, page })),
        [setFilters]
    );

    const handleSort = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            page: 1,
            keyword: debouncedKeyword,
            orderBy: !!debouncedKeyword ? prevState.orderBy : ORDER_BY.subscriptionId,
            direction: !!debouncedKeyword ? prevState.direction : sortDirection.DESC,
        }));
    }, [debouncedKeyword, setFilters]);

    return (
        <React.Fragment>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.USERS}
                </Typography>
                <Box display="flex" alignItems="flex-end" flexDirection="column">
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        onClick={() => navigate('invite')}
                    >
                        {texts.CREATE_USER}
                    </ActionButton>
                </Box>
            </Box>
            <Box display="flex" marginY={3}>
                <QuickSearchField
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    handleClear={() => setKeyword('')}
                />
            </Box>
            {!listLoader && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {tableHeads.map(th => (
                            <col key={th.id} width={th.width} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSort}
                    />
                    {listLoader || rolesLoader || companyLoading || userList.length === 0 ? (
                        <EmptyTableBody
                            colSpan={tableHeads.length}
                            loading={listLoader || rolesLoader || companyLoading}
                        />
                    ) : (
                        <TableBody>
                            {userList?.map(user => (
                                <DistributorUserRow
                                    key={user.id}
                                    {...user.subscriptions[0]}
                                    user={user}
                                    internalUser={internalUser}
                                    companyDetails={companyDetails}
                                />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default DistributorUsersTable;
